import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { exportToExcel } from "@/lib/utils/export";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { useSalesExplorer } from "@/hooks/useSalesExplorer";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { useItems } from "@/hooks/useItems";
import { useCategories } from "@/hooks/useCategories";
import { useSpecies, useBreeds } from "@/hooks/useLookups";
import { usePetSizeCategories } from "@/hooks/usePetGroomingProfile";
import { SalesExplorerFiltersPanel, type SalesExplorerOptions } from "./SalesExplorerFilters";
import { SalesExplorerPeriodBar } from "./SalesExplorerPeriodBar";
import { SalesExplorerResults } from "./SalesExplorerResults";
import { buildExplorerExportRows } from "./export-rows";
import { parseFilters, serializeFilters } from "./filters-url";
import { resolveExplorerFormat } from "./resolve-format";
import { resolvePeriodRange, type PeriodPreset } from "./period-presets";
import { EMPTY_FILTERS, type SalesExplorerFilters } from "./types";

const APPOINTMENT_STATUSES = [
  { key: "statusAgendado", value: "agendado" },
  { key: "statusConfirmado", value: "confirmado" },
  { key: "statusEmAtendimento", value: "em_atendimento" },
  { key: "statusFinalizado", value: "finalizado" },
  { key: "statusCancelado", value: "cancelado" },
  { key: "statusFaltou", value: "faltou" },
];

function useClinicProfessionals() {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    enabled: Boolean(clinicId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_clinic_professionals" as never,
        { p_clinic_id: clinicId } as never,
      );
      if (error) throw error;
      return (data ?? []) as { id: string; full_name: string | null }[];
    },
    queryKey: ["clinic-professionals", clinicId],
  });
}

export function SalesExplorerTab() {
  const { t } = useTranslation(["reports", "calendar"]);
  const [searchParams, setSearchParams] = useSearchParams();

  const defaults = useMemo<SalesExplorerFilters>(
    () => ({ ...EMPTY_FILTERS, ...resolvePeriodRange("month") }),
    [],
  );

  const filters = useMemo(() => parseFilters(searchParams, defaults), [defaults, searchParams]);

  const handleChange = useCallback(
    (next: SalesExplorerFilters) => {
      setSearchParams(serializeFilters(next), { replace: true });
    },
    [setSearchParams],
  );

  const handlePeriodChange = useCallback(
    (period: PeriodPreset) => {
      handleChange({ ...filters, period, ...resolvePeriodRange(period), page: 1 });
    },
    [filters, handleChange],
  );

  const handleCustomRangeChange = useCallback(
    (customFrom: Date, customTo: Date) => {
      handleChange({ ...filters, from: customFrom.toISOString(), to: customTo.toISOString(), page: 1 });
    },
    [filters, handleChange],
  );

  const { data, error, isLoading } = useSalesExplorer(filters);

  const { data: appointmentTypes } = useClinicAppointmentTypes();
  const { data: professionals } = useClinicProfessionals();
  const { data: items } = useItems({ isActive: true });
  const { data: categories } = useCategories({ isActive: true });
  const { data: species } = useSpecies();
  const { data: breeds } = useBreeds();
  const { data: sizeCategories } = usePetSizeCategories();

  const options: SalesExplorerOptions = useMemo(
    () => ({
      appointmentStatuses: APPOINTMENT_STATUSES.map(({ key, value }) => ({
        label: t(key, { ns: "calendar" }),
        value,
      })),
      appointmentTypes: (appointmentTypes ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
      breeds: (breeds ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
      categories: (categories ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
      items: (Array.isArray(items) ? items : []).map((entry) => ({ id: entry.id, name: entry.name })),
      professionals: (professionals ?? []).map((entry) => ({ id: entry.id, name: entry.full_name ?? entry.id })),
      sizeCategories: (sizeCategories ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
      species: (species ?? []).map((entry) => ({ id: entry.id, name: entry.name })),
    }),
    [appointmentTypes, breeds, categories, items, professionals, sizeCategories, species, t],
  );

  const handleExport = useCallback(() => {
    if (!data) return;

    exportToExcel({
      fileName: t("salesExplorerFileName"),
      rows: buildExplorerExportRows(data, {
        appointmentType: t("salesExplorerColAppointmentType"),
        date: t("salesExplorerColDate"),
        item: t("salesExplorerColItem"),
        itemCount: t("salesExplorerColItemCount"),
        itemType: t("salesExplorerColItemType"),
        owner: t("salesExplorerColOwner"),
        pet: t("salesExplorerColPet"),
        quantity: t("salesExplorerColQuantity"),
        unitPrice: t("salesExplorerColUnitPrice"),
        value: t("salesExplorerTotalValue"),
      }),
      sheetName: t("salesExplorerSheetName"),
    });
  }, [data, t]);

  return (
    // Sem Card envolvendo tudo: mesma estrutura das outras abas de Relatórios
    // (AppointmentsPeriodHero, RevenuePeriodHero, StockPeriodHero) — blocos
    // irmãos e autocontidos, separados por espaçamento, não um único card
    // grande. Título e descrição do explorador agora vivem no eyebrow e no
    // subtítulo da própria SalesExplorerPeriodBar, então não se repetem aqui.
    <div className="space-y-5">
      <SalesExplorerPeriodBar
        from={filters.from}
        onCustomRangeChange={handleCustomRangeChange}
        onPeriodChange={handlePeriodChange}
        period={filters.period}
        to={filters.to}
      />

      <SalesExplorerFiltersPanel filters={filters} onChange={handleChange} options={options} />
      <SalesExplorerResults
        error={error as Error | null}
        expectedFormat={resolveExplorerFormat(filters)}
        exportDisabled={!data || data.rows.length === 0}
        loading={isLoading}
        onExport={handleExport}
        onPageChange={(page) => handleChange({ ...filters, page })}
        page={filters.page}
        pageSize={filters.pageSize}
        result={data}
      />
    </div>
  );
}
